import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileText, ImageIcon, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS } from '@pms/shared';
import {
  assetsService,
  type AssetDetail,
  type AssetDocument,
  type AssetListItem,
} from '@/services/assets.service';
import { usePermission } from '@/hooks/usePermission';
import { getErrorMessage } from '@/lib/errors';
import { compressFilesToWebP } from '@/lib/imageUtils';
import { AuthImage } from '@/components/ui/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Notice } from '@/components/ui/notice';
import { FileDropZone } from './FileDropZone';

type DocType = AssetDocument['document_type'];

interface Props {
  asset: AssetListItem | null;
  onClose: () => void;
  /** Self-service (My Assets): fetch through `getMyAsset`, serve files from
   *  `/assets/my/documents`, show photos only, and never offer upload. */
  selfService?: boolean;
}

const IMAGE_EXT = /\.(jpe?g|png|webp|gif)$/i;
const isImage = (doc: AssetDocument) =>
  doc.mime_type ? doc.mime_type.startsWith('image/') : IMAGE_EXT.test(doc.original_name);

/** Photos and invoice/bill of one asset, side by side; editors can upload more. */
export function AssetFilesDialog({ asset, onClose, selfService = false }: Props) {
  const queryClient = useQueryClient();
  const canEdit = usePermission().has(PERMISSIONS.ASSETS_EDIT);
  const canUpload = !selfService && canEdit;
  const [preview, setPreview] = useState<AssetDocument | null>(null);

  const basePath = selfService ? '/assets/my/documents' : '/assets/documents';
  const fetchAsset: (id: string) => Promise<AssetDetail> = selfService ? assetsService.getMyAsset : assetsService.getAsset;
  const fileUrl = (doc: AssetDocument) => `${basePath}/${doc.id}/file`;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['assets', selfService ? 'my-detail' : 'detail', asset?.id],
    queryFn: () => fetchAsset(asset!.id),
    enabled: !!asset,
  });

  const upload = useMutation({
    mutationFn: async ({ files, type }: { files: File[]; type: DocType }) => {
      const processed = await compressFilesToWebP(files);
      await Promise.all(processed.map((f) => assetsService.uploadDocument(asset!.id, f, type)));
    },
    onSuccess: (_, { type }) => {
      toast.success(type === 'photo' ? 'Photo uploaded' : 'Invoice / bill uploaded');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'detail', asset?.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const download = (doc: AssetDocument) =>
    assetsService.downloadDocument(doc.id, doc.original_name, basePath).catch((e) => toast.error(getErrorMessage(e)));

  const handleClose = () => {
    setPreview(null);
    onClose();
  };

  const photos = data?.documents.filter((d) => d.document_type === 'photo') ?? [];
  const invoices = data?.documents.filter((d) => d.document_type === 'invoice') ?? [];
  const gridClass = selfService ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2';

  return (
    <Dialog open={!!asset} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {selfService ? 'Photos' : 'Photos & Invoice'} — <code className="font-mono">{asset?.asset_code}</code>
          </DialogTitle>
          <DialogDescription>
            {[asset?.brand?.name, asset?.model_name].filter(Boolean).join(' / ') || asset?.category?.name || '—'}
          </DialogDescription>
        </DialogHeader>

        {preview ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center overflow-hidden rounded-lg bg-black/80">
              <AuthImage
                src={fileUrl(preview)}
                alt={preview.original_name}
                className="max-h-[65vh] w-full object-contain"
                fallback={<p className="py-16 text-sm text-muted-foreground">File could not be loaded</p>}
              />
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button variant="outline" onClick={() => setPreview(null)}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button variant="outline" onClick={() => void download(preview)}>
                <Download className="h-4 w-4" /> Download
              </Button>
            </div>
          </div>
        ) : isLoading ? (
          <div className={gridClass}>
            {(selfService ? [0] : [0, 1]).map((i) => (
              <div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : isError ? (
          <Notice
            tone="danger"
            compact
            action={<Button size="sm" variant="outline" onClick={() => void refetch()}>Retry</Button>}
          >
            Could not load this asset's files.
          </Notice>
        ) : (
          <div className={gridClass}>
            <FileSection
              title="Photos"
              icon={ImageIcon}
              docs={photos}
              fileUrl={fileUrl}
              emptyText="No photos yet."
              onOpen={(doc) => setPreview(doc)}
              upload={canUpload ? (
                <FileDropZone
                  files={[]}
                  onFilesChange={(files) => files.length > 0 && upload.mutate({ files, type: 'photo' })}
                  accept="image/*"
                  multiple
                  placeholder="Click to upload photos (multiple allowed)"
                />
              ) : null}
              uploading={upload.isPending && upload.variables?.type === 'photo'}
            />
            {!selfService && (
              <FileSection
                title="Invoice / Bill"
                icon={Receipt}
                docs={invoices}
                fileUrl={fileUrl}
                pending
                emptyText="No invoice / bill uploaded yet."
                onOpen={(doc) => (isImage(doc) ? setPreview(doc) : void download(doc))}
                upload={canUpload ? (
                  <FileDropZone
                    files={[]}
                    onFilesChange={(files) => files.length > 0 && upload.mutate({ files, type: 'invoice' })}
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    placeholder="Click to upload invoice / bill"
                  />
                ) : null}
                uploading={upload.isPending && upload.variables?.type === 'invoice'}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FileSection({
  title,
  icon: Icon,
  docs,
  fileUrl,
  pending = false,
  emptyText,
  onOpen,
  upload,
  uploading,
}: {
  title: string;
  icon: typeof Receipt;
  docs: AssetDocument[];
  fileUrl: (doc: AssetDocument) => string;
  /** Highlight an empty section as an outstanding item rather than just "none". */
  pending?: boolean;
  emptyText: string;
  onOpen: (doc: AssetDocument) => void;
  upload: React.ReactNode;
  uploading: boolean;
}) {
  const isPending = pending && docs.length === 0;

  return (
    <section className="space-y-3 rounded-lg border border-border/60 p-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">{title}</h3>
        {isPending ? (
          <Badge className="ml-auto border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-300">
            Pending
          </Badge>
        ) : (
          <span className="ml-auto text-xs text-muted-foreground">{docs.length}</span>
        )}
      </div>

      {docs.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {docs.map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => onOpen(doc)}
              title={doc.original_name}
              className="group relative h-20 overflow-hidden rounded-md border border-border/60 bg-muted/30 transition hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {isImage(doc) ? (
                <AuthImage
                  src={fileUrl(doc)}
                  alt={doc.original_name}
                  className="h-full w-full object-cover"
                  fallback={<FileTile name={doc.original_name} />}
                />
              ) : (
                <FileTile name={doc.original_name} />
              )}
            </button>
          ))}
        </div>
      ) : isPending ? (
        <Notice tone="warning" compact icon={Receipt}>{emptyText}</Notice>
      ) : (
        <p className="py-3 text-center text-xs text-muted-foreground">{emptyText}</p>
      )}

      {uploading ? (
        <div className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary/40 p-5 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
        </div>
      ) : (
        upload
      )}
    </section>
  );
}

function FileTile({ name }: { name: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-1">
      <FileText className="h-6 w-6 text-primary" />
      <span className="w-full truncate text-[10px] text-muted-foreground">{name}</span>
    </div>
  );
}
